import "./ObjectList.css";

import { useState, useEffect } from "react";

import { SearchOutlined, DownOutlined } from "@ant-design/icons";
import { Button, Select, Input, Tooltip, Dropdown } from "antd";
import ReactPaginate from "react-paginate";

import { WarningIcon } from "../assets/icons";
import { useIsReviewer } from "../context/RoleContext";
import { getValue } from "../utils";
import { ReviewerAllowedActions } from "../utils/Constants";

const { Option } = Select;

export const ConfirmationDialog = ({ message, onConfirm, onCancel }) => {
  const handleConfirm = (event) => {
    onConfirm({ event });
  };

  const handleCancel = (event) => {
    onCancel(event);
  };

  return (
    <>
      <h1>Are you sure?</h1>
      <p>{message}</p>
      <div className="dialog-buttons">
        <button className="btn cancel" onClick={(event) => handleCancel(event)}>
          Cancel
        </button>
        <button className="btn" onClick={(event) => handleConfirm(event)}>
          Continue
        </button>
      </div>
    </>
  );
};

const PendingIdentitiesObjectList = ({
  title,
  description,
  columns,
  actions,
  globalActions,
  data,
  pageSize = 10,
  onAction,
  onGlobalAction,
  loading = false,
  hasMore = false,
  totalCount = 0,
  currentPage = 1,
  onFilterChange,
  onPageChange,
  showFilterDropdown = true,
  searchTerm: externalSearchTerm = "",
}) => {
  // Reviewers have read-only access: hide every mutating control.
  const isReviewer = useIsReviewer();
  // Reviewers keep only read-only "view" actions; all mutating actions and
  // global "create" actions are hidden for them.
  const visibleActions = isReviewer ? (actions || []).filter((a) => ReviewerAllowedActions.includes(a.name)) : actions;
  const visibleGlobalActions = isReviewer ? (globalActions || []).filter((a) => ReviewerAllowedActions.includes(a.name)) : globalActions;

  const [pageData, setPageData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [tempFilter, setTempFilter] = useState("all");
  const [pageCount, setPageCount] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Seed the search box from a parent-provided term (e.g. a global-search deep
  // link). Mirrors the Approved tab so the active term stays visible/editable
  // and pagination keeps filtering by it.
  useEffect(() => {
    setTempSearchTerm(externalSearchTerm || "");
    setSearchTerm(externalSearchTerm || "");
  }, [externalSearchTerm]);

  const handleViewResults = () => {
    setSearchTerm(tempSearchTerm);
    setSelectedFilter(tempFilter);
    if (onFilterChange) {
      onFilterChange(tempSearchTerm, tempFilter, 1);
    } else if (onPageChange) {
      onPageChange(1, tempSearchTerm);
    }
  };

  const handleFilterChange = (value) => {
    setTempFilter(value);
  };

  const handleSearchInputChange = (e) => {
    setTempSearchTerm(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleViewResults();
    }
  };

  const handleAction = (event, action, confirm, object) => {
    event.preventDefault();

    if (confirm) {
      const handleConfirm = (event) => {
        onAction(event, action, object);
        setShowDialog(false);
        setDialogContent(null);
      };

      setShowDialog(true);
      setDialogContent(
        <ConfirmationDialog title={title} message={confirm} onConfirm={(event) => handleConfirm(event)} onCancel={() => handleCancel()} />
      );
    } else if (onAction) {
      onAction(event, action, object);
    }
  };

  const handleCancel = () => {
    setShowDialog(false);
    setDialogContent(null);
  };

  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    if (onFilterChange) {
      onFilterChange(searchTerm, selectedFilter, newPage);
    } else if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleGlobalActionClick = (event, actionName, confirmation) => {
    if (confirmation) {
      const handleConfirm = (event) => {
        onGlobalAction(event, actionName);
        setShowDialog(false);
        setDialogContent(null);
      };

      setShowDialog(true);
      setDialogContent(
        <ConfirmationDialog
          title={title}
          message={confirmation}
          onConfirm={(event) => handleConfirm(event)}
          onCancel={() => handleCancel()}
        />
      );
    } else if (onGlobalAction) {
      onGlobalAction(event, actionName);
    }
  };

  useEffect(() => {
    setPageData(data);
    setPageCount(Math.ceil(totalCount / pageSize));
  }, [data, totalCount, pageSize, showDialog]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="container">
      <header className="table-header">
        <h1>{title}</h1>
        <h2>{description ? description : "A list of your " + title}</h2>
        <section className="controls">
          {/* Custom Filter Controls */}
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
            {/* Only show dropdown for Pending tab */}
            {showFilterDropdown && (
              <Select value={tempFilter} onChange={handleFilterChange} style={{ width: 350 }} placeholder="Select filter">
                <Option value="all">All Pending Users</Option>
                <Option value="kyc_wallet">Verification + Wallet Completed</Option>
                <Option value="kyc_only">Verification Completed (Wallet Incomplete)</Option>
                <Option value="email_only">Email Verified Only</Option>
              </Select>
            )}

            <Input
              placeholder="Search by name, email, address, or KYC ID"
              value={tempSearchTerm}
              onChange={handleSearchInputChange}
              onKeyPress={handleKeyPress}
              style={{ width: 500 }}
              prefix={<SearchOutlined />}
              allowClear
            />

            <Button type="primary" onClick={handleViewResults} loading={loading} className="mr-11">
              View Results
            </Button>
          </div>

          {visibleGlobalActions && (
            <div className="global-actions" style={{ marginLeft: "auto" }}>
              {visibleGlobalActions.map((globalAction) => (
                <button
                  key={globalAction.name}
                  className={"btn global-action-" + globalAction.name}
                  onClick={(event) => handleGlobalActionClick(event, globalAction.name, globalAction.confirmation)}
                >
                  {globalAction.icon ? (
                    <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      {globalAction.icon}
                      {globalAction.label}
                    </span>
                  ) : (
                    globalAction.label
                  )}
                </button>
              ))}
            </div>
          )}
        </section>
      </header>

      {totalCount > 0 && (
        <div style={{ padding: "0.5rem 0", color: "var(--text-secondary)", fontSize: "0.9rem" }}>
          Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
          {title.toLowerCase()}
        </div>
      )}

      <table>
        <thead>
          <tr>
            {columns.map((column) => {
              let fieldName = column;
              let label = column;
              let style = {};

              if (typeof column === "object") {
                fieldName = column.name;
                label = column.label;
                style = column.width ? { width: column.width } : {};
              }

              return (
                <th key={fieldName} style={style}>
                  {label}
                </th>
              );
            })}
            {visibleActions && visibleActions.length > 0 && <th key={title + "-actions"}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {pageData.map((record) => (
            <tr key={record.id}>
              {columns.map((column) => {
                let fieldName = typeof column === "object" ? column.name : column;
                let key = fieldName + "-" + record.id;
                if (column.name !== "flagged_account")
                  return <td key={key}>{column.render ? <>{column.render(record[column.name], record)}</> : <>{getValue(fieldName, record)}</>}</td>;
                else
                  return (
                    <td key={key}>
                      {record.watchlistMatched && (
                        <div className="w-7 text-red-700">
                          <WarningIcon />
                        </div>
                      )}
                      {record.pepMatched && (
                        <div className="w-7 text-red-700">
                          <WarningIcon />
                        </div>
                      )}
                    </td>
                  );
              })}
              {visibleActions && visibleActions.length > 0 && (
                <td key={"actions" + record.id}>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    {visibleActions.map((action) => {
                      if (action.type === "dropdown") {
                        const menuItems = (action.items || []).map((item) => ({
                          key: String(item.name),
                          label: item.label,
                        }));
                        return (
                          <Dropdown
                            key={record.id + "-action-" + action.name}
                            trigger={["click"]}
                            menu={{
                              items: menuItems,
                              onClick: ({ key, domEvent }) => {
                                const item = (action.items || []).find((i) => String(i.name) === key);
                                if (item) {
                                  handleAction(domEvent, item.name, item.confirmation, record);
                                }
                              },
                            }}
                          >
                            <button
                              onClick={(event) => event.preventDefault()}
                              style={{
                                color: "var(--link-color)",
                                transition: "0.5s all",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "0.25rem",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "0.25rem",
                                fontSize: "1.1rem",
                              }}
                              aria-label={action.label}
                            >
                              {action.icon}
                              <span style={{ fontSize: "0.9rem" }}>{action.label}</span>
                              <DownOutlined style={{ fontSize: "0.7rem" }} />
                            </button>
                          </Dropdown>
                        );
                      }

                      const isDisabled = typeof action.disabled === "function" ? action.disabled(record) : false;

                      const buttonContent = (
                        <button
                          key={record.id + "-action-" + action.name}
                          onClick={(event) => {
                            if (isDisabled) {
                              event.preventDefault();
                              return;
                            }
                            handleAction(event, action.name, action.confirmation, record);
                          }}
                          disabled={isDisabled}
                          style={{
                            color: isDisabled ? "var(--text-secondary, #9ca3af)" : "var(--link-color)",
                            transition: "0.5s all",
                            background: "none",
                            border: "none",
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            opacity: isDisabled ? 0.45 : 1,
                            padding: "0.25rem",
                            display: "inline-flex",
                            alignItems: "center",
                            fontSize: "1.1rem",
                          }}
                          aria-label={action.label}
                          aria-disabled={isDisabled}
                        >
                          {action.icon || action.label}
                        </button>
                      );

                      const tooltipTitle = isDisabled ? action.disabledTooltip || "Already reset — awaiting re-verification" : action.label;

                      return action.icon ? (
                        <Tooltip key={record.id + "-action-" + action.name} title={tooltipTitle} placement="top">
                          {buttonContent}
                        </Tooltip>
                      ) : (
                        buttonContent
                      );
                    })}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {pageData.length === 0 && (isLoading || loading) && (
        <div className="empty">
          <p>Loading {title}...</p>
        </div>
      )}

      {pageData.length === 0 && !isLoading && !loading && (
        <div className="empty">
          <p>No {title.toLowerCase()} found.</p>
        </div>
      )}

      <div className="pagination">
        <ReactPaginate
          breakLabel="&#8230;"
          previousLabel="&#9664;"
          nextLabel="&#9658;"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          renderOnZeroPageCount={null}
          forcePage={currentPage - 1}
        />
      </div>

      {showDialog && (
        <div className="dialog">
          <div className="dialog-content">{dialogContent}</div>
        </div>
      )}
    </div>
  );
};

export default PendingIdentitiesObjectList;
